import React, { useContext, useState, useMemo, memo, useCallback, useRef, useEffect } from "react";

import {
	Space,		
	Drawer,
	Button,
	message,
	Modal,
	Tooltip,
} from "antd";

import {
	CloudUploadOutlined,	
	PlusOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import PropTypes from "prop-types";

import { useCalculateListSizeForPaginationHook } from "../../../../hooks";

import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { edit_bank_transactions } from "../../../../services";
import { BankAccountContext } from "../../../../contexts";

import { formatCurrency, filterTransactionsToShow } from "../../utils";

import { TableBankTransactions } from "./TableBankTransactions";
import { ModalQuit, ModalTransactionView } from "./ModalsForBankTransactions";
import { FilterTransaction } from "./FilterTransaction";
import { BankReconciliationForTransactionModal } from "./BankReconciliationForTransactionModal";

import "../../../../styles/selectActions.css";
import { SaveOptions } from "./SaveOptions";
import { ClassificationModal } from "./ClassificationModal";
import { SaveState, SaveStateTransactions } from "./SaveStateTransactions";

const TransactionState = Object.freeze({
	ARCHIVED: "archived",
	UNVERIFIED: "unverified",
	VERIFIED: "verified",
});

const isValidTheBankTransactionFunction = (transactionCheckData) => {
	const { classification = "" , client_id = null, supplier_id = null, note = "" } = transactionCheckData;
	
	if(classification === ""){
		return { isValid: false, customMessage: "Porfavor implementa una 'clasificación'" };
	}
	
	if(client_id == null && supplier_id == null){
		const entity = transactionCheckData.position === "in" ? "Cliente" : "Proveedor";			
		return { isValid: false, customMessage: `Porfavor implementa un '${entity}'` };
	}

	if(note === ""){
		return { isValid: false, customMessage: "Porfavor implementa una 'nota'" };
	}
	
	return { isValid: true, customMessage: "" };
};

export const BankTransactionsSection = memo(({
	token,	
	bankTransactionsData,
	backupBankTransactionsData,
	loading = false,
	lastMonthsDetected,
	onUpdateSingleRegister = () => {},
	onBankTransactionsData = () => {},	
	onBackupBankTransactionsData = () => {},	
	onCloseBankAccount = () => {},
	onOpenCreateTransaction = () => {},
	onOpenConvertTemplate = () => {},	
	onFilterTransactionsProps = () => {},
	updateData = () => {},
}) => {
	const modalQuitRef = useRef(null);
	const modalTransactionViewRef = useRef(null);
	const modalBankReconciliationRef = useRef(null);
	const modalSaveOptions = useRef(null);
	const modalClassificationRef = useRef(null);
	const transactionSaveStateRef = useRef(null);
			
	const { pageSize } = useCalculateListSizeForPaginationHook(62);
		
	const [openBasePointsInformation, setOpenBasePointsInformation] = useState(false);
		
	const { bankAccountSelected, updateBasePointsFromBankAccountSelected, editedTransactions, setEditedTransactions } = useContext(BankAccountContext);
	const [transactionsEditedProccessing, setTransactionsEditedProccessing] = useState({});
	const [dataToProcess, setDataToProcess] = useState({
		transactionsToEdit: [],
		failedTransactions: [],
	});
	const allBasePoints = {...bankAccountSelected.basePoints ?? {}, ...bankAccountSelected.temporalBasePoints ?? {}};	

	useEffect(() => {
		if (Object.keys(editedTransactions).length > 0) {
			modalSaveOptions.current?.methodToRun(resetCountdown);
		}
	}, [editedTransactions]);

	useEffect(() => {
		const getDifference = (transactionsInEdition, transactionsToEdit) => {
			const result = {};
					
			Object.keys(transactionsInEdition).forEach((transactionId) => {
				const editedTransaction = transactionsInEdition[transactionId];
				const originalTransaction = transactionsToEdit[transactionId];
		
				if (!originalTransaction) {		
					result[transactionId] = editedTransaction;
				} else {					
					const diff = {};
		
					Object.keys(editedTransaction).forEach((key) => {
						if (editedTransaction[key] !== originalTransaction[key]) {
							diff[key] = editedTransaction[key];
						}
					});
		
					if (Object.keys(diff).length > 0) {
						result[transactionId] = diff;
					}
				}
			});
		
			return result;
		};
		
		const process = () => {
			const transactionsInEdition = {...editedTransactions};
			const { transactionsToEdit, failedTransactions, transactionsTransformed } = dataToProcess;
							
			if(Object.keys(transactionsToEdit).length === 0) return;
			
			const difference = getDifference(transactionsInEdition, transactionsToEdit);
			
			const updatedEditedTransactions = Object.keys(transactionsToEdit)
				.filter((key) => failedTransactions.includes(parseInt(key)))
				.reduce((acc, key) => {
					acc[key] = transactionsToEdit[key];
					return acc;		
				}, {});
	
			const finalEditedTransactions = { ...updatedEditedTransactions, ...difference };
	
			const updatedTransactionsTransformed = transactionsTransformed.map((transaction) => {
				if (difference[transaction.id]) {					
					return {
						...transaction,
						...difference[transaction.id],
					};
				}
				return transaction;
			});
				
			Object.keys(difference).forEach((transactionId) => {
				const exists = updatedTransactionsTransformed.some((t) => t.id === transactionId);
				if (!exists) {
					updatedTransactionsTransformed.push({ id: transactionId, ...difference[transactionId] });
				}
			});

			setEditedTransactions(finalEditedTransactions);										
			setTransactionsEditedProccessing({});
			onBankTransactionsData(updatedTransactionsTransformed);
			onBackupBankTransactionsData(updatedTransactionsTransformed);

			setDataToProcess({
				transactionsToEdit: [],
				failedTransactions: [],
			});
		};

		process();
	}, [dataToProcess]);
	
	const sortedWithSaldo = useMemo(() => {
		if (!bankAccountSelected) return [];
	
		// Ordenar por fecha y luego por table_index
		const ascendingTransactions = [...bankTransactionsData].sort((a, b) => {
			const aDate = a.date.split(" ")[0];
			const bDate = b.date.split(" ")[0];

			const dateDiff = new Date(aDate) - new Date(bDate);
			if (dateDiff !== 0) return dateDiff;
	
			// Si las fechas son iguales, usar table_index (y fallback a id si no existe)
			return (a.table_index ?? 0) - (b.table_index ?? 0);
		});
	
		// Calcular saldo acumulativo
		let runningBalance = bankAccountSelected.initial_amount || 0;
	
		// Ajustar saldo inicial según la fecha
		if (ascendingTransactions.length > 0) {
			const firstTransaction = ascendingTransactions[0];
			const firstDate = new Date(firstTransaction.date);
			const year = firstDate.getFullYear();
			const month = firstDate.getMonth();
			const previousMonth = month === 0 ? 11 : month - 1;
			const previousYear = month === 0 ? year - 1 : year;
			const previousMonthKey = `${previousYear}-${String(previousMonth + 1).padStart(2, "0")}`;
	
			if (allBasePoints?.[previousMonthKey] !== undefined) {
				runningBalance = allBasePoints[previousMonthKey];
			}
		}
	
		// Aplicar saldos en orden
		const transactionsWithSaldo = ascendingTransactions.map((transaction) => {
			const amount =
				transaction?.position === "in" ? transaction?.amount : -transaction?.amount;
	
			// Incrementar el saldo si la transacción no está archivada
			if (transaction?.state !== TransactionState.ARCHIVED) {
				runningBalance += amount;
			}
	
			return {
				...transaction,
				saldo: runningBalance,
			};
		});
	
		return transactionsWithSaldo;
	}, [bankTransactionsData, bankAccountSelected, allBasePoints]);
			
	// For Automatic Mode for send changes
	const [sendingChanges, setSendingChanges] = useState(false);		
	
	const [timer, setTimer] = useState(null);
	
	const startCountdown = () => {
		let seconds = 5;

		const newTimer = setInterval(() => {
			seconds -= 1;

			if (seconds <= 0) {
				clearInterval(newTimer);
				if (seconds === 0) sendChanges();				
			}
		}, 1000);

		setTimer(newTimer);
		transactionSaveStateRef.current?.changeState(SaveState.TO_SAVE);
	};
	
	const resetCountdown = () => {
		if (timer) {
			clearInterval(timer);
		}
		startCountdown();
	};
	
	const updateBankTransactionData = useCallback((attribute, value, record) => {
		onUpdateSingleRegister(attribute, value, record);		
	}, []);

	const rowClassName = (record, attribute) => {
		if (!editedTransactions[record.id]) {
			return "";
		}

		const editedValue = editedTransactions[record.id][attribute];
		const positionHasBeChanged = editedTransactions[record.id]["position"];
		const classificationIdHasBeChanged = editedTransactions[record.id]["classification_id"];

		if (positionHasBeChanged && (attribute === "client_id" || attribute === "supplier_id")){
			return editedValue === null || editedValue === "" ? "warning" : "edited-row";
		}

		if(classificationIdHasBeChanged && attribute === "classification_id"){
			return "warning";
		}

		return editedTransactions[record.id][attribute] ? "edited-row" : "";
	};

	const sendChanges = async () => {
		if(Object.keys(editedTransactions).length === 0){
			message.error("No se detectaron cambios realizados");
			return;
		}
		
		try {
			const transactionsToEdit = {...editedTransactions};

			setSendingChanges(true);
			setTransactionsEditedProccessing(transactionsToEdit);
			transactionSaveStateRef.current?.changeState(SaveState.SAVING);
			
			const dataForEditTransactions = {
				bank_account_id: bankAccountSelected.id,
				transactions: transactionsToEdit,
			};
			
			const res = await edit_bank_transactions(token, "edit", dataForEditTransactions);

			if (res) {
				const { 
					// message: messageFromRes, 
					failedTransactions, 
					transactionsSendedToVerify, 
					updatedBasePoints,
					updatedTemporalBasePoints,
				} = res;

				// Convert the strings one detected to dayjs
				const updatedData = bankTransactionsData.map((transaction) => {
					if (transactionsSendedToVerify.includes(transaction.id)) {
						return {
							...transaction,
							date: typeof transaction.date === "string" ? dayjs(transaction.date, "YYYY-MM-DD HH:mm:ss") : transaction.date,
							state: "under-review",
						};
					}

					return {
						...transaction,
						date: typeof transaction.date === "string" ? dayjs(transaction.date, "YYYY-MM-DD HH:mm:ss") : transaction.date,
					};
				});
									
				// Filter if the dates of the transactions in in the consulted allready transactions
				const transactionsFiltered = filterTransactionsToShow(updatedData, lastMonthsDetected);

				const transactionsTransformed = transactionsFiltered.map((transaction) => {
					const { date } = transaction;
					
					return {
						...transaction,
						date: date.format("YYYY-MM-DD HH:mm:ss"),
					};
				});
																
				updateBasePointsFromBankAccountSelected(updatedBasePoints, updatedTemporalBasePoints, updateData);

				setDataToProcess({
					transactionsToEdit,
					failedTransactions,
					transactionsTransformed,
				});
				
				transactionSaveStateRef.current?.changeState(SaveState.SAVED);
			} else {
				setTransactionsEditedProccessing({});
			}
		} catch (err) {
			setTransactionsEditedProccessing({});
			message.error(err.response.data);
			transactionSaveStateRef.current?.changeState(SaveState.ERROR);
		} finally {
			setSendingChanges(false);
			clearInterval(timer);
			setTimeout(() => {
				transactionSaveStateRef.current?.changeState(SaveState.EMPTY);
			}, 3000);
		}
	};

	const fetchVerifyBankTransaction = async(selectedTransaction) => {		
		if(editedTransactions[selectedTransaction?.id]){
			message.error("Porfavor asegurate de guardar los cambios realizados de esta transacción antes de guardar/verificar");
			return;
		}

		// ? ===> Rules to set
		const { isValid, customMessage } = isValidTheBankTransactionFunction(selectedTransaction);

		if(!isValid){
			message.error(customMessage);
			return;
		}
		
		// ? ===> Sent to verify the Invoice

		try {
			const dataForVerify = {
				bank_account_id: bankAccountSelected.id,
				transactions: [selectedTransaction.id],
			};
			
			setSendingChanges(true);
			const res = await edit_bank_transactions(token, "verify", dataForVerify);			

			if(res){
				const { message: messageFromRes } = res;

				const updatedData = bankTransactionsData.map((transaction) => {
					if(transaction.id === selectedTransaction.id){
						return { ...transaction, state: "verified" };
					}
					
					return { ...transaction };
				});

				onBankTransactionsData(updatedData);
				onBackupBankTransactionsData(updatedData);
				message.success(messageFromRes);
			}
		} catch (err) {
			message.error(err.response.data);
		} finally {
			setSendingChanges(false);
		}
	};

	return (
		<Drawer
			title={`${bankAccountSelected?.bank} ${bankAccountSelected?.account_number}`}
			placement="right"
			width={"100%"}
			open={bankAccountSelected ? true : false}
			onClose={() => {
				const editedTransactionsCounter = Object.keys(editedTransactions).length;

				if(editedTransactionsCounter !== 0){
					modalQuitRef.current?.openModal();
					return;
				}
								
				onCloseBankAccount(null);
				setEditedTransactions({});				
				modalBankReconciliationRef.current?.clearInvoices();
			}}
			extra={
				<Space>	
					<SaveStateTransactions ref={transactionSaveStateRef}/>

					<FilterTransaction
						loading={loading}
						onFilterTransactions={onFilterTransactionsProps}
						isTransactionsEdited={Object.keys(editedTransactions).length !== 0}
					/>

					<Button
						type="primary"
						icon={<CloudUploadOutlined />}
						onClick={() => {
							onOpenConvertTemplate(true);
						}}
						loading={loading}
						style={{ marginLeft: "25px" }}
						ghost
					>
						Subir Plantilla
					</Button>
										
					<SaveOptions
						ref={modalSaveOptions}
						loading={loading}
						sendingChanges={sendingChanges}
						disableSave={Object.keys(editedTransactions).length === 0}
						onSendChanges={sendChanges}
					/>
				</Space>
			}
		>
			<>
				<TableBankTransactions
					token={token}
					bankTransactionsData={bankTransactionsData}
					backupBankTransactionsData={backupBankTransactionsData}
					sortedWithSaldo={sortedWithSaldo}
					editedTransactions={editedTransactions}
					transactionsEditedProccessing={transactionsEditedProccessing}
					allBasePoints={allBasePoints}										
					lastMonthsDetected={lastMonthsDetected}
					loading={loading}					
					sendChanges={sendingChanges}					
					onBankTransactionsData={onBankTransactionsData}
					onBackupBankTransactionsData={onBackupBankTransactionsData}
					rowClassName={rowClassName}
					updateBankTransactionData={updateBankTransactionData}
					onEditedTransactions={setEditedTransactions}
					onSelectedTransaction={(value) => modalTransactionViewRef.current?.onSetTransaction(value)}
					onSelectedTransactionForReconciliation={async(value) => modalBankReconciliationRef.current?.onSetTransaction(value)}
					updateData={updateData}
					onOpenClassifications={modalClassificationRef.current?.openModal}
				>					
					<Tooltip title="Crear Transacción">
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								onOpenCreateTransaction(true);
							}}
							loading={loading}
							ghost
						>
						</Button>
					</Tooltip>
				</TableBankTransactions>
										
				<Modal
					title="Registros Detectados"
					open={openBasePointsInformation}
					onCancel={() => setOpenBasePointsInformation(false)}
					onOk={() => setOpenBasePointsInformation(false)}
				>												
					<ul
						style={{
							maxHeight: `${pageSize * 30}px`,
							overflowY: "auto",
							margin: 0,
							padding: 0,
						}}
					>
						{Object.keys(allBasePoints).map((basePoint, index) => (
							<li style={{ padding: "10px 0", listStyle: "none" }} key={index}>
								{"►"} {basePoint} : {currencyGetter[bankAccountSelected?.currency]} {formatCurrency(allBasePoints[basePoint])}
							</li>
						))}
					</ul>
				</Modal>
				
				<ModalQuit
					ref={modalQuitRef}
					onConfirm={() => {
						onCloseBankAccount(null);
						setEditedTransactions({});
						modalBankReconciliationRef.current?.clearInvoices();
					}}
				/>
				
				<ModalTransactionView
					ref={modalTransactionViewRef}
					onConfirm={fetchVerifyBankTransaction}
					bankAccountSelected={bankAccountSelected}
				/>

				<BankReconciliationForTransactionModal
					ref={modalBankReconciliationRef}
					token={token}
					bankAccountSelected={bankAccountSelected}
					bankTransactionsData={bankTransactionsData}
					onUpdateTransactionsData={(value) => {						
						onBankTransactionsData(value);
						onBackupBankTransactionsData(value);
					}}
				/>

				<ClassificationModal
					ref={modalClassificationRef}
					token={token}					
				/>
			</>
		</Drawer>
	);
});

BankTransactionsSection.displayName = "BankTransactionsSection";

BankTransactionsSection.propTypes = {
	token: PropTypes.string.isRequired,
	bankTransactionsData: PropTypes.array.isRequired,
	backupBankTransactionsData: PropTypes.array.isRequired,
	loading: PropTypes.bool,
	lastMonthsDetected: PropTypes.shape({
		firstMonthDetected: "",
		lastMonthDetected: "",
	}),	
	onUpdateSingleRegister: PropTypes.func,
	onBankTransactionsData: PropTypes.func,
	onBackupBankTransactionsData: PropTypes.func,
	onCloseBankAccount: PropTypes.func,
	onOpenCreateTransaction: PropTypes.func,
	onOpenConvertTemplate: PropTypes.func,
	onFilterTransactionsProps: PropTypes.func,
	updateData: PropTypes.func.isRequired,
};
